// Contract address rules (SPEC section 4, api-surface section 3).
// - Solana addresses are base58 and case sensitive: the case is preserved.
//   An all-lowercase "Solana" address has lost its case (232 archive rows in
//   the 2026-08-17 pull) and cannot be verified: Address unverified.
// - EVM addresses are hex: compared lowercased, displayed as given.
// - coin-search fills name with "Contract <8 chars>" and symbol with
//   "UNRESOLVED" when metadata is absent (contract 4.1 forbids showing these
//   as identity). Both are sentinels and are stripped before display.

export type ContractChain = 'solana' | 'evm';
export type AddressState = 'exact' | 'unverified';

const EVM_ADDRESS = /^0x[a-fA-F0-9]{40}$/u;
const SOLANA_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;
const SOLANA_CASE_LOST = /^[a-z0-9]+$/u;
const SENTINEL_NAME = /^Contract [1-9A-Za-z0-9]{8}$/u;
const SENTINEL_SYMBOL = 'UNRESOLVED';

export interface ContractDescription {
  readonly input: string;
  readonly chain: ContractChain | null;
  // Exact string to render (mono). Never re-cased.
  readonly display: string;
  // Key for equality: EVM lowercased, Solana as given.
  readonly compareKey: string;
  readonly isValid: boolean;
  readonly addressState: AddressState;
  readonly reason: string | null;
}

export function detectChain(address: string): ContractChain | null {
  if (EVM_ADDRESS.test(address)) return 'evm';
  if (SOLANA_ADDRESS.test(address)) return 'solana';
  return null;
}

export function isContractAddress(value: string): boolean {
  return detectChain(value.trim()) !== null;
}

export function normalizeForCompare(address: string): string {
  const trimmed = address.trim();
  return detectChain(trimmed) === 'evm' ? trimmed.toLowerCase() : trimmed;
}

export function sameContract(a: string, b: string): boolean {
  return normalizeForCompare(a) === normalizeForCompare(b);
}

export function describeContract(address: string): ContractDescription {
  const trimmed = address.trim();
  const chain = detectChain(trimmed);
  if (chain === null) {
    return {
      input: address,
      chain: null,
      display: trimmed,
      compareKey: trimmed,
      isValid: false,
      addressState: 'unverified',
      reason: 'not_a_contract_address',
    };
  }
  if (chain === 'solana' && SOLANA_CASE_LOST.test(trimmed)) {
    return {
      input: address,
      chain,
      display: trimmed,
      compareKey: trimmed,
      isValid: true,
      addressState: 'unverified',
      reason: 'solana_address_case_lost',
    };
  }
  return {
    input: address,
    chain,
    display: trimmed,
    compareKey: chain === 'evm' ? trimmed.toLowerCase() : trimmed,
    isValid: true,
    addressState: 'exact',
    reason: null,
  };
}

// Short form for tight cells: first 5, ellipsis character, last 4. The full
// address stays available through describeContract().display.
export function shortContract(address: string, head = 5, tail = 4): string {
  const trimmed = address.trim();
  if (trimmed.length <= head + tail + 1) return trimmed;
  return `${trimmed.slice(0, head)}…${trimmed.slice(-tail)}`;
}

export function isSentinelName(value: string | null | undefined): boolean {
  return typeof value === 'string' && SENTINEL_NAME.test(value.trim());
}

export function isSentinelSymbol(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().toUpperCase() === SENTINEL_SYMBOL;
}

export interface IdentityInput {
  readonly contract: string;
  readonly name?: string | null;
  readonly symbol?: string | null;
  // Archive rows carry the server's resolution verdict; commercial reads do not.
  readonly contractResolution?: 'exact_contract' | 'unresolved' | null;
}

export type MetadataState = 'available' | 'unavailable';

export interface TokenIdentity {
  readonly name: string | null;
  readonly symbol: string | null;
  // The address itself is a valid contract shape (or the server said exact).
  readonly exactContract: boolean;
  readonly addressState: AddressState;
  readonly metadataState: MetadataState;
  readonly contract: ContractDescription;
}

// Strips sentinels and reports whether real metadata exists. A valid address
// counts as resolved even when metadata is missing (SPEC section 4: Coin
// Checker treats a valid address as resolved when research-cache market is {}).
export function resolveTokenIdentity(input: IdentityInput): TokenIdentity {
  const contract = describeContract(input.contract);
  const name = input.name && !isSentinelName(input.name) ? input.name.trim() || null : null;
  const symbol = input.symbol && !isSentinelSymbol(input.symbol) ? input.symbol.trim() || null : null;
  const exactContract = input.contractResolution === 'unresolved'
    ? false
    : input.contractResolution === 'exact_contract' || contract.isValid;
  return {
    name,
    symbol,
    exactContract,
    addressState: exactContract ? contract.addressState : 'unverified',
    metadataState: name || symbol ? 'available' : 'unavailable',
    contract,
  };
}
